import { useTranslation } from '@/core/hooks';
import { toLocalReservationSignatureStatus } from '@/core/reservation';
import { useAppStore } from '@/core/stores/appStore';
import { logger } from '@/core/logger';
import { trackAdhaReservationCardClick, trackCatalogEvent } from '@/core/services/trackDynatraceCatalog';
import { ALMETLAA_ACTION, ALMETLAA_SCREEN } from '@/core/constants/dynatraceCatalog';
import { fetchPropertyAssets } from '@/core/services/contentfulClient';
import { resolveDocumentDownloadUrls } from '@/core/utils/brochureDownload';
import type { AvailableUnit, UnitDetailsData } from '@/core/api/models/properties';
import {
  DOCUMENT_SF_NAMES,
  DOCUMENT_TYPES,
} from '@/modules/profile/constants/documentTypes';
import { downloadAndPersistDocument } from '@/modules/property-details/utils/documentDownload';
import { checkFloorPlanAvailability } from '@/modules/property-details/utils/propertyDocuments';
import * as Linking from 'expo-linking';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ImageSourcePropType, Platform, useWindowDimensions } from 'react-native';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getUnitDetailsLabels } from '../constants/getPropertyLabels';
import {
  resolveWebReservationMappedStatus,
  resolveWebReservationRawStatus,
} from '../constants/webReservationStatus';
import { getUnitDetailsLayout } from '../details/unitDetailsLayout';
import {
  mapUnitDetailsDataToUi,
  mergeListingWithProjectDetails,
  parsePositiveLoanAmountAed,
} from '../services/mapUnitDetails';
import { bookingService } from '../services/bookingService';
import type { UnitDetails } from '../types/property.types';
import type { ReservationSignatureStatus } from '../components/ReservationStatusCard';
import { isAdhaProfileActionEnabled } from '../utils/adhaProfileActions';
import { isAppointmentBookingWindowClosed } from '@/modules/book-appointment/utils/slotDates';
import { resolveProfileIskanLoanAmountAed } from '../utils/iskanLoan';
import {
  applyAdhaBookingApiResult,
  formatBookingRequestedOn,
  resolveReservationCardDateDisplay,
  resolveReservationRequestedOnDisplay,
} from '../utils/syncAdhaBookingFromApi';
import { useSyncProfileDetailsToStore } from '@/modules/auth/hooks/useSyncProfileDetailsToStore';
import { useDeepLinkedUnitLookup } from '@/modules/properties-web/hooks/useDeepLinkedUnitLookup';
import {
  mapProfileDetailsToUserPatch,
  profileService,
} from '@/modules/auth/services/profileService';
import type { ProfileDetailsResponse } from '@/core/api/models/profile';
import {
  readListingUnitModel,
  resolveUnitListingImages,
} from '../utils/pickMixedUnitImages';

/**
 * Resolve the catalogue row for unit details:
 * 1) current listing replica (`availableUnits`)
 * 2) sticky reserved snapshot (`pendingUnitReservation.unit`) when this is the booked unit
 * 3) session snapshot (details opened via View Reservation while unit is off the filtered list)
 */
function resolveListingUnit(
  unitId: string | undefined,
  availableUnits: ReturnType<typeof useAppStore.getState>['availableUnits'],
  pendingUnitReservation: ReturnType<
    typeof useAppStore.getState
  >['pendingUnitReservation'],
  sessionSnapshot?: AvailableUnit | null,
) {
  if (!unitId) {
    return undefined;
  }

  const fromList = availableUnits.find((u) => u.unit_id === unitId);
  const reservedSnapshot =
    pendingUnitReservation?.unitId === unitId
      ? pendingUnitReservation
      : null;
  const reservedStatus =
    reservedSnapshot?.reservationStatus?.trim() ||
    reservedSnapshot?.unit?.reservation_status?.trim() ||
    '';

  if (fromList) {
    const reservedModel = reservedSnapshot?.unit.unit_model?.trim();
    return {
      ...fromList,
      ...(reservedModel ? { unit_model: reservedModel } : {}),
      ...(reservedStatus ? { reservation_status: reservedStatus } : {}),
    };
  }

  if (reservedSnapshot) {
    return reservedSnapshot.unit;
  }

  if (sessionSnapshot?.unit_id === unitId) {
    return sessionSnapshot;
  }

  return undefined;
}

/**
 * Floor plan is only offered when the project documents advertise one. ADHA
 * sends the Salesforce name under `name`, older payloads under `title`.
 */
function hasFloorPlanDocument(
  documents: UnitDetailsData['documents'],
): boolean {
  const list = documents ?? [];
  if (list.length === 0) {
    return false;
  }
  if (checkFloorPlanAvailability(list as any)) {
    return true;
  }
  return list.some((doc) => doc?.title === DOCUMENT_SF_NAMES.FLOOR_PLAN);
}

/**
 * Unit details screen data:
 * - Listing row (price, beds, baths, area, coords) from `availableUnits`
 * - Amenities / project / loan context from `adhaProjectDetails`
 *   (refetched on each web load, not on opening this screen)
 */
export function usePropertiesDetails() {
  const { unitId, viewReservation } = useLocalSearchParams<{
    unitId?: string;
    viewReservation?: string;
  }>();
  const router = useRouter();
  const queryClient = useQueryClient();
  const { width } = useWindowDimensions();
  const { isCompact } = getUnitDetailsLayout(width);
  const { t, currentLanguage } = useTranslation();
  const contentfulLocale = currentLanguage === 'ar' ? 'ar' : 'en';
  const unitLabels = useMemo(() => getUnitDetailsLabels(t), [t]);
  const [unitImages, setUnitImages] = useState<ImageSourcePropType[]>([]);
  /** True once the gallery for the current unit is final (images, none, or failed). */
  const [unitImagesResolved, setUnitImagesResolved] = useState(false);
  const [contentfulProjectName, setContentfulProjectName] = useState<
    string | undefined
  >();
  const [contentfulDescription, setContentfulDescription] = useState<
    string | undefined
  >();
  const [downloadingDocumentId, setDownloadingDocumentId] = useState<
    string | null
  >(null);
  const [floorPlanLoading, setFloorPlanLoading] = useState(false);
  const [floorPlanSuccessMessage, setFloorPlanSuccessMessage] = useState<
    string | null
  >(null);
  const [floorPlanSavedUri, setFloorPlanSavedUri] = useState<string | null>(
    null,
  );
  const [floorPlanErrorMessage, setFloorPlanErrorMessage] = useState<
    string | null
  >(null);
  const [activeReservationErrorVisible, setActiveReservationErrorVisible] =
    useState(false);
  const [documentErrorMessage, setDocumentErrorMessage] = useState<
    string | null
  >(null);
  const [reservationRequestPending, setReservationRequestPending] =
    useState(false);
  /** Shimmer while GET /v1/bookings runs after reserve/cancel success. */
  const [bookingDetailSyncPending, setBookingDetailSyncPending] =
    useState(false);

  const availableUnits = useAppStore((s) => s.availableUnits);
  const customerId = useAppStore((s) => s.auth.user?.customer_id);
  const projectDetails = useAppStore((s) => s.adhaProjectDetails);
  const pendingUnitReservation = useAppStore((s) => s.pendingUnitReservation);
  const activeBookedUnitId = useAppStore((s) => s.activeBookedUnitId);
  const setPendingUnitReservation = useAppStore(
    (s) => s.setPendingUnitReservation,
  );
  const clearAdhaWebBookingState = useAppStore((s) => s.clearAdhaWebBookingState);
  const upsertAvailableUnitFirst = useAppStore((s) => s.upsertAvailableUnitFirst);
  /** From GET /v1/customers/profile-details (store patch) */
  const profileLoanAmount = useAppStore((s) => s.auth.user?.loan_Amount);
  const lastBookingResponseRef = useRef<
    Awaited<ReturnType<typeof bookingService.getBookingDetails>> | null
  >(null);

  const profileQuery = useQuery({
    queryKey: ['adhaProfileDetails', customerId],
    queryFn: () => profileService.getProfileDetails(),
    enabled: Boolean(customerId),
    retry: false,
    staleTime: 5 * 60 * 1000,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });

  useSyncProfileDetailsToStore(
    profileQuery.data,
    profileQuery.isSuccess,
  );

  const profileLoanFromQuery = useMemo(() => {
    const data = profileQuery.data;
    if (!data) {
      return undefined;
    }
    return (
      parsePositiveLoanAmountAed(data.loan_Amount) ??
      parsePositiveLoanAmountAed(data.loan_amount)
    );
  }, [profileQuery.data]);

  /** Profile loan for confirm / price breakdown — missing → 0, never catalogue. */
  const profileIskanLoanAmount = useMemo(
    () =>
      resolveProfileIskanLoanAmountAed(
        profileLoanFromQuery,
        profileLoanAmount,
      ),
    [profileLoanFromQuery, profileLoanAmount],
  );

  const applyBookingGetToStore = useCallback(
    (
      response: Awaited<ReturnType<typeof bookingService.getBookingDetails>>,
      profileDetails?: ProfileDetailsResponse | null,
    ) => {
      lastBookingResponseRef.current = response;
      const profileData =
        profileDetails !== undefined ? profileDetails : (profileQuery.data ?? null);
      applyAdhaBookingApiResult({
        response,
        availableUnits: useAppStore.getState().availableUnits,
        resolveUnitTitle: (row) => `${row.unit_number || ''}`,
        formatRequestedOn: formatBookingRequestedOn,
        profileDetails: profileData,
        setPendingUnitReservation,
        clearAdhaWebBookingState,
        getPreviousPending: () => useAppStore.getState().pendingUnitReservation,
        upsertAvailableUnitFirst,
      });
    },
    [
      clearAdhaWebBookingState,
      profileQuery.data,
      setPendingUnitReservation,
      upsertAvailableUnitFirst,
    ],
  );

  /** After reserve/cancel: GET booking + profile-details (for current_status). */
  const refreshBookingDetailAfterMutation = useCallback(async () => {
    if (!customerId) {
      return;
    }
    setBookingDetailSyncPending(true);
    try {
      const [response, profileDetails] = await Promise.all([
        bookingService.getBookingDetails(customerId),
        profileService.getProfileDetails(),
      ]);
      useAppStore.getState().setProfile(mapProfileDetailsToUserPatch(profileDetails));
      applyBookingGetToStore(response, profileDetails);
      await Promise.all([
        queryClient.resetQueries({ queryKey: ['adhaBooking', customerId] }),
        queryClient.resetQueries({ queryKey: ['adhaProfileDetails', customerId] }),
      ]);
    } catch (error) {
      logger.warn('usePropertiesDetails', 'Post-mutation booking/profile GET failed', {
        error,
      });
    } finally {
      setBookingDetailSyncPending(false);
    }
  }, [applyBookingGetToStore, customerId, queryClient]);

  const clearViewReservationQueryParam = useCallback(() => {
    if (!unitId || viewReservation == null) {
      return;
    }
    router.replace(
      `/properties-web/details?unitId=${encodeURIComponent(unitId)}` as never,
    );
  }, [router, unitId, viewReservation]);

  // Re-sync booking on details mount / customer change (not on every units length change).
  useEffect(() => {
    if (!customerId) return;

    let mounted = true;
    const syncBooking = async () => {
      try {
        const response = await bookingService.getBookingDetails(customerId);
        if (!mounted) return;
        applyBookingGetToStore(response);
      } catch (error) {
        if (!mounted) return;
        logger.warn('usePropertiesDetails', 'Booking sync failed — clearing local reservation', {
          error,
        });
        clearAdhaWebBookingState();
      }
    };

    void syncBooking();
    return () => {
      mounted = false;
    };
  }, [customerId, clearAdhaWebBookingState, applyBookingGetToStore]);

  useEffect(() => {
    if (!profileQuery.isSuccess || !lastBookingResponseRef.current) {
      return;
    }
    applyBookingGetToStore(lastBookingResponseRef.current);
  }, [applyBookingGetToStore, profileQuery.data, profileQuery.isSuccess]);

  // Survives cancel when pending is cleared and a filtered units refetch drops this row.
  const detailsListingSnapshotRef = useRef<AvailableUnit | null>(null);
  if (
    detailsListingSnapshotRef.current &&
    unitId &&
    detailsListingSnapshotRef.current.unit_id !== unitId
  ) {
    detailsListingSnapshotRef.current = null;
  }

  const listingUnit = useMemo(() => {
    const resolved = resolveListingUnit(
      unitId,
      availableUnits,
      pendingUnitReservation,
      detailsListingSnapshotRef.current,
    );
    if (resolved) {
      const { reservation_status: _status, ...catalogueRow } = resolved;
      const hasActiveBooking =
        pendingUnitReservation?.unitId === unitId ||
        activeBookedUnitId === unitId;
      detailsListingSnapshotRef.current = hasActiveBooking
        ? resolved
        : catalogueRow;
    }
    return resolved;
  }, [
    activeBookedUnitId,
    availableUnits,
    pendingUnitReservation,
    unitId,
  ]);

  const unit: UnitDetails | undefined = useMemo(() => {
    if (!unitId || !listingUnit) {
      return undefined;
    }
    const merged = mergeListingWithProjectDetails(listingUnit, projectDetails);
    const mapped = mapUnitDetailsDataToUi(merged, unitId);
    // SF status only while this unit is the active booking (not leftover catalogue stamp).
    const isActiveBookingForUnit =
      pendingUnitReservation?.unitId === unitId ||
      activeBookedUnitId === unitId;
    const reservationStatus = isActiveBookingForUnit
      ? resolveWebReservationRawStatus(listingUnit)
      : undefined;
    // Profile-details loan only; missing → 0 (never catalogue sanctioned_loan_amount).
    const preApprovalAmount = profileIskanLoanAmount;
    return {
      ...mapped,
      preApprovalAmount,
      ...(reservationStatus ? { reservationStatus } : {}),
    };
  }, [
    unitId,
    listingUnit,
    projectDetails,
    profileIskanLoanAmount,
    pendingUnitReservation?.unitId,
    activeBookedUnitId,
  ]);

  const { isLookingUp: isLookingUpUnit } = useDeepLinkedUnitLookup(
    unitId,
    Boolean(unitId && !listingUnit),
  );
  const unitNotFound = Boolean(unitId && !listingUnit && !isLookingUpUnit);
  const isLoading = isLookingUpUnit;
  // Already-painted images stay visible while a refetch (e.g. locale) runs.
  const unitImagesLoading =
    unitImages.length === 0 && !unitImagesResolved && !unitNotFound;
  // Hide Reserve when API says another unit is booked (unit need not be on this listing page).
  const hasOtherActiveReservation = Boolean(
    activeBookedUnitId && unitId && activeBookedUnitId !== unitId,
  );
  const isUnitReserveEnabled = isAdhaProfileActionEnabled(
    profileQuery.data?.isUnitReserveEnabled,
  );
  const isAppointmentBookingEnabled =
    isAdhaProfileActionEnabled(profileQuery.data?.isAppointmentBookingEnabled) &&
    !isAppointmentBookingWindowClosed(
      profileQuery.data?.appointmentBookingStartDate,
      profileQuery.data?.appointmentBookingEndDate,
    );
  const isReserveDisabled = hasOtherActiveReservation || !isUnitReserveEnabled;
  const listingUnitModel = readListingUnitModel(listingUnit);
  const listingUnitReady = Boolean(listingUnit?.unit_id);

  useEffect(() => {
    let cancelled = false;

    const loadUnitImages = async () => {
      setUnitImagesResolved(false);
      const projectId =
        projectDetails?.project_id ||
        useAppStore.getState().adhaLoanDetails?.projectId;

      if (!projectId) {
        if (!cancelled) {
          setUnitImages([]);
          setUnitImagesResolved(true);
        }
        return;
      }

      try {
        const assets = await fetchPropertyAssets(projectId, {
          maxWidth: 1200,
          locale: contentfulLocale,
        });
        if (cancelled) {
          return;
        }

        const unitPool = assets.unitImages ?? [];
        logger.info('usePropertiesDetails', 'propertyImages unitImages field', {
          projectId,
          propertyImageCount: assets.images.length,
          unitImageCount: unitPool.length,
          unitImageUrls: unitPool.map((img) => img.url),
        });

        // Wait for the catalogue row so a listing that arrives after this
        // fetch cannot be painted with the unitImages fallback first.
        const mixed =
          unitId && listingUnitReady
            ? resolveUnitListingImages(
                assets.unitImagesByUnitModel,
                listingUnitModel,
                unitId,
              )
            : [];
        setUnitImages(mixed);
        // A unit whose catalogue row hasn't arrived yet reruns this effect once it does.
        setUnitImagesResolved(!unitId || listingUnitReady);
        setContentfulProjectName(assets.name?.trim() || undefined);
        setContentfulDescription(assets.description?.trim() || undefined);
      } catch (error) {
        if (cancelled) {
          return;
        }
        logger.error('usePropertiesDetails', 'Failed to fetch unit images', {
          unitId,
          error,
        });
        setUnitImages([]);
        setUnitImagesResolved(true);
        setContentfulProjectName(undefined);
        setContentfulDescription(undefined);
      }
    };

    void loadUnitImages();
    return () => {
      cancelled = true;
    };
  }, [
    projectDetails?.project_id,
    contentfulLocale,
    unitId,
    listingUnitModel,
    listingUnitReady,
  ]);

  const handleMapPress = useCallback(() => {
    if (!unit) {
      return;
    }
    if (!unit.latitude || !unit.longitude) {
      logger.info('usePropertiesDetails', 'No coordinates for unit', {
        unitId: unit.id,
      });
      return;
    }

    const { latitude, longitude } = unit;
    let mapUrl: string;
    if (Platform.OS === 'ios') {
      mapUrl = `http://maps.apple.com/?q=${latitude},${longitude}`;
    } else if (Platform.OS === 'android') {
      mapUrl = `geo:${latitude},${longitude}?q=${latitude},${longitude}`;
    } else {
      mapUrl = `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`;
    }

    Linking.openURL(mapUrl).catch((error) => {
      logger.error('usePropertiesDetails', 'Failed to open maps', { error });
    });
  }, [unit]);

  const handleBookAppointment = useCallback(() => {
    if (!unit) {
      return;
    }
    logger.info('usePropertiesDetails', 'Book appointment pressed', {
      unitId: unit.id,
    });
  }, [unit]);

  const [confirmReservationVisible, setConfirmReservationVisible] =
    useState(false);
  const [unitReservedSuccessVisible, setUnitReservedSuccessVisible] =
    useState(false);
  const [isUnitReserved, setIsUnitReserved] = useState(
    pendingUnitReservation?.unitId === unitId,
  );
  
  useEffect(() => {
    setIsUnitReserved(pendingUnitReservation?.unitId === unitId);
  }, [pendingUnitReservation?.unitId, unitId]);

  // Active booking for this unit only — leftover catalogue reservation_status is ignored.
  const isThisUnitActiveBooking =
    isUnitReserved ||
    pendingUnitReservation?.unitId === unitId ||
    Boolean(unitId && activeBookedUnitId === unitId);
  const isReservedForUi = isThisUnitActiveBooking;
  const [reservationSigned, setReservationSigned] = useState(false);
  const [cancelReservationVisible, setCancelReservationVisible] =
    useState(false);
  const [reservationCanceledVisible, setReservationCanceledVisible] =
    useState(false);
  const [reservationSummaryVisible, setReservationSummaryVisible] =
    useState(false);

  // Bottom sheet is phone/tablet only; desktop uses the sidebar status card.
  useEffect(() => {
    if (viewReservation === '1' && isReservedForUi && isCompact) {
      setReservationSummaryVisible(true);
    }
  }, [isCompact, isReservedForUi, viewReservation]);

  useEffect(() => {
    if (!isCompact) {
      setReservationSummaryVisible(false);
    }
  }, [isCompact]);

  let localSignatureStatus: 'confirmed' | 'check_email' | undefined;
  if (reservationSigned) {
    localSignatureStatus = 'confirmed';
  } else if (isThisUnitActiveBooking) {
    localSignatureStatus = 'check_email';
  }
  const reservationMappedStatus = isThisUnitActiveBooking
    ? resolveWebReservationMappedStatus(listingUnit, {
        isLocallyReserved: true,
        ...(localSignatureStatus ? { localSignatureStatus } : {}),
      })
    : null;
  let reservationSignatureStatus: ReservationSignatureStatus =
    'awaiting_signature';
  if (reservationMappedStatus) {
    reservationSignatureStatus = toLocalReservationSignatureStatus(
      reservationMappedStatus,
    );
  }

  const reservedDateDisplay = useMemo(
    () =>
      unitId
        ? resolveReservationCardDateDisplay(
            unitId,
            profileQuery.data,
            pendingUnitReservation,
          )
        : '',
    [pendingUnitReservation, profileQuery.data, unitId],
  );

  const requestedOnDisplay = useMemo(
    () =>
      unitId
        ? resolveReservationRequestedOnDisplay(
            unitId,
            profileQuery.data,
            pendingUnitReservation,
          )
        : '',
    [pendingUnitReservation, profileQuery.data, unitId],
  );

  const handleReserveUnit = useCallback(() => {
    if (!unit) {
      return;
    }
    if (isReservedForUi) {
      logger.info('usePropertiesDetails', 'Cancel reservation pressed', {
        unitId: unit.id,
      });
      trackCatalogEvent({
        screen: ALMETLAA_SCREEN.RESERVE,
        actionName: ALMETLAA_ACTION.RESERVE.CANCEL_DIALOG_OPEN,
        eventType: 'action',
      });
      setCancelReservationVisible(true);
      return;
    }
    if (!isUnitReserveEnabled) {
      logger.info('usePropertiesDetails', 'Reserve blocked — profile isUnitReserveEnabled is not true', {
        unitId: unit.id,
      });
      return;
    }
    if (hasOtherActiveReservation) {
      logger.info('usePropertiesDetails', 'Reserve blocked — active reservation', {
        unitId: unit.id,
        reservedUnitId: pendingUnitReservation?.unitId,
      });
      setActiveReservationErrorVisible(true);
      return;
    }
    logger.info('usePropertiesDetails', 'Reserve unit pressed', {
      unitId: unit.id,
    });
    setConfirmReservationVisible(true);
  }, [
    unit,
    isReservedForUi,
    isUnitReserveEnabled,
    hasOtherActiveReservation,
    pendingUnitReservation?.unitId,
  ]);

  const handleCloseActiveReservationError = useCallback(() => {
    setActiveReservationErrorVisible(false);
  }, []);

  const handleCloseDocumentError = useCallback(() => {
    setDocumentErrorMessage(null);
  }, []);

  const handleViewReservation = useCallback(() => {
    if (!unit || !isCompact) {
      return;
    }
    logger.info('usePropertiesDetails', 'View reservation pressed', {
      unitId: unit.id,
    });
    trackAdhaReservationCardClick(unit.id);
    setReservationSummaryVisible(true);
  }, [isCompact, unit]);

  const handleCloseReservationSummary = useCallback(() => {
    setReservationSummaryVisible(false);
  }, []);

  const handleCloseConfirmReservation = useCallback(() => {
    setConfirmReservationVisible(false);
  }, []);

  const handleProceedReservation = useCallback(async () => {
    if (!unit || !listingUnit || reservationRequestPending) {
      return;
    }
    logger.info('usePropertiesDetails', 'Proceed reservation pressed', {
      unitId: unit.id,
    });
    try {
      setReservationRequestPending(true);
      await bookingService.createBooking(unit.id, customerId);
      // Success first — then sync booking GET (shimmer) in the background.
      setConfirmReservationVisible(false);
      setIsUnitReserved(true);
      setReservationSigned(false);
      setUnitReservedSuccessVisible(true);
      setReservationRequestPending(false);
      clearViewReservationQueryParam();
      void refreshBookingDetailAfterMutation();
    } catch (error: unknown) {
      const apiMessage =
        error instanceof Error && error.message.trim()
          ? error.message.trim()
          : '';
      const isUnitUnavailable = /no longer available/i.test(apiMessage);
      const message = isUnitUnavailable
        ? unitLabels.TOAST_UNIT_UNAVAILABLE
        : unitLabels.TOAST_RESERVE_FAILED;
      logger.error('usePropertiesDetails', 'Reserve unit failed', {
        unitId: unit.id,
        error,
      });
      if (Platform.OS === 'web') {
        setConfirmReservationVisible(false);
      }
      setDocumentErrorMessage(message);
      setReservationRequestPending(false);
    }
  }, [
    clearViewReservationQueryParam,
    customerId,
    listingUnit,
    refreshBookingDetailAfterMutation,
    reservationRequestPending,
    unit,
    unitLabels,
  ]);

  const handleCloseUnitReservedSuccess = useCallback(() => {
    setUnitReservedSuccessVisible(false);
  }, []);

  const handleCloseCancelReservation = useCallback(() => {
    setCancelReservationVisible(false);
  }, []);

  const handleConfirmCancelReservation = useCallback(async () => {
    if (!unit || reservationRequestPending) {
      return;
    }
    logger.info('usePropertiesDetails', 'Confirm cancel reservation', {
      unitId: unit.id,
    });
    try {
      setReservationRequestPending(true);
      // Same POST /v1/bookings as reserve; empty unit_id cancels the lead's booking.
      await bookingService.cancelBooking(customerId);
      // Success first — drop booking state immediately so CTAs flip off leftover
      // catalogue reservation_status; GET booking confirms in the background.
      clearAdhaWebBookingState();
      setReservationCanceledVisible(true);
      setIsUnitReserved(false);
      setReservationSigned(false);
      setReservationSummaryVisible(false);
      setCancelReservationVisible(false);
      setReservationRequestPending(false);
      clearViewReservationQueryParam();
      void refreshBookingDetailAfterMutation();
    } catch (error: unknown) {
      logger.error('usePropertiesDetails', 'Cancel reservation failed', {
        unitId: unit.id,
        error,
      });
      setCancelReservationVisible(false);
      setDocumentErrorMessage(unitLabels.TOAST_CANCEL_FAILED);
      setReservationRequestPending(false);
    }
  }, [
    clearAdhaWebBookingState,
    clearViewReservationQueryParam,
    customerId,
    refreshBookingDetailAfterMutation,
    reservationRequestPending,
    unit,
    unitLabels,
  ]);

  const handleCloseReservationCanceled = useCallback(() => {
    setReservationCanceledVisible(false);
  }, []);

  const isFloorPlanAvailable = useMemo(
    () => hasFloorPlanDocument(projectDetails?.documents),
    [projectDetails?.documents],
  );

  const handleViewFloorPlan = useCallback(async () => {
    if (!unit) {
      return;
    }
    logger.info('usePropertiesDetails', 'View floor plan pressed', {
      unitId: unit.id,
    });

    if (!customerId) {
      logger.warn('usePropertiesDetails', 'Floor plan needs a customer id', {
        unitId: unit.id,
      });
      setFloorPlanErrorMessage(
        unitLabels.TOAST_DOWNLOAD_FAILED,
      );
      return;
    }

    try {
      setFloorPlanLoading(true);

      const downloadResult = await downloadAndPersistDocument({
        docType: DOCUMENT_TYPES.FLOOR_PLAN,
        parentId: unit.id,
        customerId,
        translationKey: 'propertyDetails.viewFloorPlan',
        t,
      });

      if (
        !downloadResult.success ||
        !downloadResult.fileUri ||
        !downloadResult.message
      ) {
        throw new Error(downloadResult.error || 'Failed to save floor plan');
      }

      setFloorPlanSavedUri(downloadResult.fileUri);
      setFloorPlanSuccessMessage(downloadResult.message);
    } catch (error: any) {
      logger.error('usePropertiesDetails', 'View floor plan failed', {
        unitId: unit.id,
        error: error?.message || error,
      });
      setFloorPlanErrorMessage(
        error?.message || unitLabels.TOAST_DOWNLOAD_FAILED,
      );
    } finally {
      setFloorPlanLoading(false);
    }
  }, [customerId, t, unit, unitLabels]);

  const handleCloseFloorPlanSuccess = useCallback(() => {
    setFloorPlanSuccessMessage(null);
    setFloorPlanSavedUri(null);
  }, []);

  const handleCloseFloorPlanError = useCallback(() => {
    setFloorPlanErrorMessage(null);
  }, []);

  const finalUnit = useMemo(() => {
    if (!unit) return undefined;
    return {
      ...unit,
      ...(contentfulProjectName
        ? { projectName: contentfulProjectName }
        : {}),
      ...(contentfulDescription
        ? { description: contentfulDescription }
        : {}),
    };
  }, [unit, contentfulProjectName, contentfulDescription]);

  const handleDocumentPress = useCallback(
    (documentId: string) => {
      if (!finalUnit || downloadingDocumentId) {
        return;
      }
      logger.info('usePropertiesDetails', 'Document pressed', {
        unitId: finalUnit.id,
        documentId,
      });

      const doc = finalUnit.documents?.find((d) => d.id === documentId);
      const urls = resolveDocumentDownloadUrls(doc);
      if (urls.length === 0) {
        return;
      }

      const projectName =
        finalUnit.projectName || finalUnit.unitNumber || 'project';

      setDownloadingDocumentId(documentId);
      void import('@/core/utils/brochureDownload')
        .then(({ downloadBrochureFiles }) =>
          downloadBrochureFiles({ urls, projectName }),
        )
        .catch((error) => {
          logger.error('usePropertiesDetails', 'Failed to download document', {
            error,
          });
          setDocumentErrorMessage(unitLabels.TOAST_DOWNLOAD_FAILED);
        })
        .finally(() => {
          setDownloadingDocumentId(null);
        });
    },
    [finalUnit, downloadingDocumentId, unitLabels.TOAST_DOWNLOAD_FAILED],
  );



  return {
    unit: finalUnit,
    unitNotFound,
    isLoading,
    unitImages,
    unitImagesLoading,
    handleMapPress,
    handleBookAppointment,
    handleReserveUnit,
    isReserveDisabled,
    activeReservationErrorVisible,
    handleCloseActiveReservationError,
    documentErrorMessage,
    handleCloseDocumentError,
    handleViewReservation,
    handleCloseReservationSummary,
    reservationSummaryVisible,
    handleCloseConfirmReservation,
    handleProceedReservation,
    confirmReservationVisible,
    handleCloseUnitReservedSuccess,
    unitReservedSuccessVisible,
    isUnitReserved: isReservedForUi,
    reservationSignatureStatus,
    reservationMappedStatus,
    reservedDateDisplay,
    requestedOnDisplay,
    cancelReservationVisible,
    reservationCanceledVisible,
    handleCloseCancelReservation,
    handleConfirmCancelReservation,
    handleCloseReservationCanceled,
    isFloorPlanAvailable,
    handleViewFloorPlan,
    floorPlanLoading,
    floorPlanSuccessMessage,
    floorPlanSavedUri,
    floorPlanErrorMessage,
    handleCloseFloorPlanSuccess,
    handleCloseFloorPlanError,
    handleDocumentPress,
    downloadingDocumentId,
    reservationRequestPending,
    bookingDetailSyncPending,
    isAppointmentBookingEnabled,
    profileIskanLoanAmount,
  };
}
